import React, { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "react-i18next";
import { AccessibilityData, Assets } from "../../../../types/types.PIM";
import { ProductContext } from "../ProductContext";
import { ChangeSection, ChangeSeparator, DiffText } from "./styles";
import { useTheme } from "styled-components";
import { AllTokenKeys, spacingTokens } from "../../../../designTokens";
import { EMPTY_VALUE } from "./utils";
import { UnifiedAttributeChange, BeforeAfterContainer } from "./Attributes";
import {
  LockIcon,
  NonVisibleIcon,
  UnLockIcon,
  VisibleIcon,
} from "../../../../components/Icons/Icons";
import { assetMetadataLabelMap, AssetNameIcon } from "../Assets/util";
import {
  AccessibilityChips,
  DocumentPreview,
} from "../../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/DocumentPreview";
import { useAuthContext } from "../../../../components/Auth";
import { getAssetMetadataDiffEntries, hasAccessibilityChanges } from "./groups";
import { ActivityHistoryValue, ValueType } from "./types";
import { getCountryOption } from "../../../../util/location";
import { useCountriesList } from "../../../../util/Locations";
import { getLanguageOption, useLanguagesList } from "../../../../util/Language";
import { useStoreState } from "../../../../util/util";

const formatUserName = (data: AccessibilityData): string => {
  const { firstname, lastname, name, fullname } = data;
  return (
    fullname ||
    `${firstname || ""} ${lastname || ""}`.trim() ||
    name ||
    "Unknown User"
  );
};

const getChipsFromAsset = (asset: Partial<Assets>, t: TFunction) => {
  const customerChips: { name: string; id: string }[] = [];
  if (asset.allow_all_customers) {
    customerChips.push({
      name: t("All Customers"),
      id: "allow_all_customers",
    });
  }
  if (asset.allow_all_distributors) {
    customerChips.push({
      name: t("All Distributors"),
      id: "allow_all_distributors",
    });
  }

  if (asset.customers?.length) {
    asset.customers.forEach((customer: AccessibilityData) =>
      customerChips.push({
        name: customer.tenant_name || customer.name || "",
        id: customer.tenant_id || customer.id || "",
      })
    );
  }

  const roleChips: { name: string; id: string }[] = [];
  if (asset.allow_all_roles) {
    roleChips.push({ name: t("All Roles"), id: "allow_all_roles" });
  } else if (asset.roles?.length) {
    asset.roles.forEach((role: AccessibilityData) =>
      roleChips.push({
        name: role.name || "",
        id: role.id,
      })
    );
  }

  const userChips: { name: string; id: string }[] = [];
  if (asset.users?.length) {
    asset.users.forEach((user: AccessibilityData) => {
      userChips.push({
        name: formatUserName(user),
        id: user.id,
      });
    });
  }

  return { customerChips, roleChips, userChips };
};

const assetMetadataTypeFor = (key: string): string => {
  if (key.endsWith("_date")) return "date";
  return "text";
};

function AssetMetadataChanges({
  beforeAsset,
  afterAsset,
  attributeName,
}: {
  beforeAsset: Assets | null;
  afterAsset: Assets | null;
  attributeName?: string;
}) {
  const { t } = useTranslation();
  const labels = assetMetadataLabelMap(t);
  const entries = getAssetMetadataDiffEntries({ beforeAsset, afterAsset });
  const hasAccessibility = hasAccessibilityChanges(beforeAsset, afterAsset);
  const countries = useCountriesList();
  const languages = useLanguagesList();

  if (entries.length === 0 && !hasAccessibility) {
    return null;
  }

  return (
    <>
      {entries.map(({ key, before, after, isName }, idx) => {
        const attrLabel = attributeName
          ? `${attributeName} - ${labels[key] || key}`
          : labels[key] || key;

        if (isName) {
          return (
            <ChangeSection key={`asset-meta-name`}>
              <AssetNameRow
                key={`asset-meta-name`}
                beforeAsset={beforeAsset}
                afterAsset={afterAsset}
                attributeName={attrLabel}
              />
              {(idx !== entries.length - 1 || hasAccessibility) && (
                <ChangeSeparator />
              )}
            </ChangeSection>
          );
        }

        const transformedBefore =
          key === "language"
            ? getLanguageOption(languages, before as string)?.label
            : key === "country"
            ? getCountryOption(countries, before as string)?.label
            : before;
        const transformedAfter =
          key === "language"
            ? getLanguageOption(languages, after as string)?.label
            : key === "country"
            ? getCountryOption(countries, after as string)?.label
            : after;

        return (
          <React.Fragment key={`asset-meta-${key}`}>
            <ChangeSection>
              <UnifiedAttributeChange
                before={
                  transformedBefore as
                    | ValueType
                    | ActivityHistoryValue
                    | ActivityHistoryValue[]
                    | null
                }
                after={
                  transformedAfter as
                    | ValueType
                    | ActivityHistoryValue
                    | ActivityHistoryValue[]
                    | null
                }
                attributeName={labels[key] || key}
                inputType={assetMetadataTypeFor(key)}
              />
            </ChangeSection>
            {(idx !== entries.length - 1 || hasAccessibility) && (
              <ChangeSeparator />
            )}
          </React.Fragment>
        );
      })}
      {hasAccessibility && (
        <ChangeSection key="asset-accessibility">
          <AccessibilityAttributeChange
            before={beforeAsset || ({} as Assets)}
            after={afterAsset || ({} as Assets)}
          />
        </ChangeSection>
      )}
    </>
  );
}

function AssetNameRow({
  beforeAsset,
  afterAsset,
  attributeName,
}: {
  beforeAsset: Assets | null;
  afterAsset: Assets | null;
  attributeName: string;
}) {
  const product = useContext(ProductContext);
  const { roleIsGuest, roleIsSomeKindOfSeller } = useAuthContext();
  const [showPreview, setShowPreview] = useState(false);
  const [previewAsset, setPreviewAsset] = useState<Assets | null>(null);

  const { slug } = useStoreState();

  const beforeName = beforeAsset?.name ?? "";
  const afterName = afterAsset?.name ?? "";
  const nameChanged = beforeName !== afterName;

  const beforeHighlight =
    beforeAsset && (!afterAsset || nameChanged) ? "removed" : null;
  const afterHighlight =
    afterAsset && (!beforeAsset || nameChanged) ? "added" : null;

  const openPreview = (asset: Assets) => {
    setPreviewAsset(asset);
    setShowPreview(true);
  };

  const renderCell = (
    asset: Assets | null,
    highlight: "added" | "removed" | null
  ) => {
    if (!asset) {
      return <span style={{ display: "inline-block" }}>{EMPTY_VALUE}</span>;
    }

    const content = (
      <AssetNameIcon
        idx={0}
        asset_type={asset.asset_type || "document"}
        name={asset.name}
        is_downloadable={
          asset.visibility === "public" ||
          (!roleIsGuest && asset.visibility === "logged_in_user") ||
          roleIsSomeKindOfSeller
        }
        is_generated={asset.is_generated || false}
        is_external_asset={asset.asset_mode === "external"}
        content_type={asset.content_type || null}
        set_show_preview={() => openPreview(asset)}
        set_selected_asset_idx={() => {}}
        disableTrim
      />
    );

    if (!highlight) {
      return content;
    }

    return (
      <DiffText
        added={highlight === "added"}
        removed={highlight === "removed"}
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: `${spacingTokens.spacing_xs} ${spacingTokens.spacing_md}`,
        }}
      >
        {content}
      </DiffText>
    );
  };

  const productAssetShareUrl = useCallback(
    (asset: Assets) => {
      if (asset.asset_mode === "external") return asset.signed_url;
      if (!product) return "";
      return `https://${window.location.hostname}/api/store/${slug}/products/${
        product.slug
      }/${asset.asset_type}/${encodeURIComponent(asset.name)}`;
    },
    [product, slug]
  );

  return (
    <>
      <BeforeAfterContainer
        attributeName={attributeName}
        beforeContent={renderCell(beforeAsset, beforeHighlight)}
        afterContent={renderCell(afterAsset, afterHighlight)}
      />
      {showPreview && previewAsset && (
        <DocumentPreview
          assets={[
            {
              ...previewAsset,
              can_edit: false,
              can_delete: false,
              can_change_visibility: false,
              can_external_download: true,
              can_view_details: true,
            } as Assets & {
              can_edit: boolean;
              can_change_visibility: boolean;
              can_delete: boolean;
              can_external_download: boolean;
              can_view_details: boolean;
            },
          ]}
          onCancelPreview={() => {
            setShowPreview(false);
            setPreviewAsset(null);
          }}
          selected_asset_idx={0}
          onDownload={(asset) => {
            const url = productAssetShareUrl(asset);
            if (url) window.open(url, "_blank");
          }}
          is_product_page={true}
        />
      )}
    </>
  );
}

export function AssetAddedChange({
  asset,
  attributeName,
}: {
  asset: Assets;
  attributeName?: string;
}) {
  const { t } = useTranslation();
  return (
    <AssetMetadataChanges
      beforeAsset={null}
      afterAsset={asset}
      attributeName={attributeName ?? t("Documents")}
    />
  );
}

export function AssetRemovedChange({
  asset,
  attributeName,
}: {
  asset: Assets;
  attributeName?: string;
}) {
  const { t } = useTranslation();
  return (
    <AssetMetadataChanges
      beforeAsset={asset}
      afterAsset={null}
      attributeName={attributeName ?? t("Documents")}
    />
  );
}

export function AssetUpdatedChange({
  before,
  after,
}: {
  before: Assets;
  after: Assets;
}) {
  return <AssetMetadataChanges beforeAsset={before} afterAsset={after} />;
}

export function AccessibilityAttributeChange({
  before,
  after,
}: {
  before: Assets;
  after: Assets;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const beforeAccessibility = before || {};
  const afterAccessibility = after || {};

  const beforeAsset: Partial<Assets> = {
    asset_type: beforeAccessibility.asset_type,
    allow_all_customers: beforeAccessibility.allow_all_customers,
    allow_all_distributors: beforeAccessibility.allow_all_distributors,
    allow_all_roles: beforeAccessibility.allow_all_roles,
    customers: beforeAccessibility.customers,
    roles: beforeAccessibility.roles,
    users: beforeAccessibility.users,
    visibility: beforeAccessibility.visibility,
  };

  const afterAsset: Partial<Assets> = {
    asset_type: afterAccessibility.asset_type,
    allow_all_customers: afterAccessibility.allow_all_customers,
    allow_all_distributors: afterAccessibility.allow_all_distributors,
    allow_all_roles: afterAccessibility.allow_all_roles,
    customers: afterAccessibility.customers,
    roles: afterAccessibility.roles,
    users: afterAccessibility.users,
    visibility: afterAccessibility.visibility,
  };

  const beforeChips = getChipsFromAsset(beforeAsset, t);
  const afterChips = getChipsFromAsset(afterAsset, t);

  const allChips = [
    ...beforeChips.customerChips,
    ...beforeChips.roleChips,
    ...beforeChips.userChips,
    ...afterChips.customerChips,
    ...afterChips.roleChips,
    ...afterChips.userChips,
  ];

  const changeMap = new Map<string, "added" | "removed" | "unchanged">();
  const beforeIds = new Set(
    [
      ...beforeChips.customerChips,
      ...beforeChips.roleChips,
      ...beforeChips.userChips,
    ].map((chip) => chip.id)
  );
  const afterIds = new Set(
    [
      ...afterChips.customerChips,
      ...afterChips.roleChips,
      ...afterChips.userChips,
    ].map((chip) => chip.id)
  );

  allChips.forEach((chip) => {
    const wasInBefore = beforeIds.has(chip.id);
    const isInAfter = afterIds.has(chip.id);

    if (!wasInBefore && isInAfter) {
      changeMap.set(chip.id, "added");
    } else if (wasInBefore && !isInAfter) {
      changeMap.set(chip.id, "removed");
    } else if (wasInBefore && isInAfter) {
      changeMap.set(chip.id, "unchanged");
    }
  });

  const getAccessibilityType = (accessibility: Partial<Assets>): string => {
    if (accessibility.visibility === "logged_in_user") {
      return t("Customers & Internal Users");
    }
    if (accessibility.visibility === "internal") {
      return t("Internal Users");
    }
    if (accessibility.visibility === "public") {
      return t("Public");
    }
    return "";
  };

  const beforeType = getAccessibilityType(beforeAsset);
  const afterType = getAccessibilityType(afterAsset);
  const typeChanged = beforeType !== afterType;

  const hasBeforeData =
    Boolean(beforeAsset.visibility) ||
    Boolean(beforeAsset.customers?.length) ||
    Boolean(beforeAsset.roles?.length) ||
    Boolean(beforeAsset.users?.length);
  const hasAfterData =
    Boolean(afterAsset.visibility) ||
    Boolean(afterAsset.customers?.length) ||
    Boolean(afterAsset.roles?.length) ||
    Boolean(afterAsset.users?.length);

  const getIconColor = (type: string): AllTokenKeys => {
    if (type === t("Public")) return "color_icon_success";
    if (type === t("Internal Users")) return "color_icon_error";
    return "color_icon_caution";
  };

  const renderAccessibilityType = (
    type: string,
    isBefore: boolean
  ): React.ReactNode => {
    const iconColor = getIconColor(type);
    const isInternal = type === t("Internal Users");
    const isDocument =
      beforeAsset?.asset_type === "document" ||
      afterAsset?.asset_type === "document";
    const Icon = isInternal
      ? isDocument
        ? LockIcon
        : NonVisibleIcon
      : isDocument
      ? UnLockIcon
      : VisibleIcon;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          marginBottom: 16,
        }}
      >
        <Icon width={20} height={20} fill={theme.resolve(iconColor)} />
        {typeChanged ? (
          <DiffText removed={isBefore} added={!isBefore}>
            {type}
          </DiffText>
        ) : (
          <span>{type}</span>
        )}
      </div>
    );
  };

  const renderAccessibilityContent = (
    asset: Partial<Assets>,
    type: string,
    isBefore: boolean,
    hasData: boolean
  ): React.ReactNode => {
    if (!hasData) return EMPTY_VALUE;

    return (
      <>
        {renderAccessibilityType(type, isBefore)}
        {asset?.asset_type === "document" && asset.visibility !== "public" && (
          <AccessibilityChips
            asset={asset as Assets}
            changeMap={changeMap}
            mode={isBefore ? "before" : "after"}
            showHighlighting={true}
          />
        )}
      </>
    );
  };

  return (
    <BeforeAfterContainer
      attributeName={t("Accessibility")}
      beforeContent={renderAccessibilityContent(
        beforeAsset,
        beforeType,
        true,
        hasBeforeData
      )}
      afterContent={renderAccessibilityContent(
        afterAsset,
        afterType,
        false,
        hasAfterData
      )}
    />
  );
}
